import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { FetchArgs, PagingTokenFixed } from 'src/app/shared/models/core';
import { LineOfBusiness } from 'src/app/shared/models/enums/line-of-business';
import { KeyValue } from 'src/app/shared/models/key-value.model';
import { QmAssociationChange } from 'src/app/shared/models/qm-association.model';
import { BulkQmAssociationAssignmentChange } from 'src/app/shared/models/user-assignment-change-request.model';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { buildHttpParams } from 'src/app/shared/utilities/interpolate-params';
import { combine } from 'src/app/shared/utilities/route';
import { CategoryLineOfBusiness } from '../enums/category-line-of-business.enum';
import { ClientPortalVisibilitySettings } from '../models/client-portal-visibility-settings.model';
import { ClientPortalVisibilityUpdate } from '../models/client-portal-visibility-update.model';
import { DocDueDate } from '../models/doc-due-date.model';
import { ProfileRequirement } from '../models/profile-requirements.model';
import { QmAssociationAutoMatchSettingsViewModel } from '../models/qm-association-auto-match-settings-dto.model';
import { QmAssociationDto, QmAssociationGridItem } from '../models/qm-association.model';

/**
 * @deprecated Do not use this service.  Use service(s) from "src/app/compliance/api" or "src/app/compliance/api/migration"
 */
@Injectable({ providedIn: 'root' })
export class QmAssociationService {
    private readonly _coreApiPath: string;

    constructor(
        private http: HttpClient,
        @Inject(APP_CONFIG) settings: Settings,
        private readonly _ldManager: LDFeatureManager
    ) {
        this._coreApiPath = combine(settings.coreAPIUrl, 'ayanova/compliance/qm-associations');
    }

    // ** QmAssociationsController.QmAssociations
    getQmAssociations(args: FetchArgs): Observable<PagingTokenFixed<QmAssociationGridItem[]>> {
        const params = {};
        buildHttpParams(params, args, '');
        return this.http.get<PagingTokenFixed<QmAssociationGridItem[]>>(this._coreApiPath, { params });
    }

    // ** QmAssociationsController.LookupQmAssociations
    getAssociations(lookUpParm: string): Observable<KeyValue<number, string>[]> {
        return this.http.get<KeyValue<number, string>[]>(`${this._coreApiPath}/lookup?lookUpParm=${lookUpParm}`);
    }

    // ** QmAssociationsController.GetQmAssociationRequirements
    getQmAssociationRequirements(
        id: number,
        lineOfBusiness: LineOfBusiness[],
        args: FetchArgs
    ): Observable<ProfileRequirement[]> {
        const lobs = lineOfBusiness.map((lob) =>
            [LineOfBusiness.Locums, LineOfBusiness.AccessControl].includes(lob) ? lob : LineOfBusiness.Travel
        );

        const url = `${this._coreApiPath}/${id}/requirements`;
        const params = { clobs: lobs.map(this.mapToCategoryLineOfBusiness) };
        buildHttpParams(params, args, '');
        return this.http.get<ProfileRequirement[]>(url, { params });
    }

    private mapToCategoryLineOfBusiness(lob: LineOfBusiness): CategoryLineOfBusiness {
        switch (lob) {
            case LineOfBusiness.Travel:
                return CategoryLineOfBusiness.Travel;
            case LineOfBusiness.Locums:
                return CategoryLineOfBusiness.Locums;
            case LineOfBusiness.AccessControl:
                return CategoryLineOfBusiness.AccessControl;
            default:
                return null;
        }
    }

    // ** QmAssociationsController.GetQmAssociationLineOfBusiness
    getQmAssociationLineOfBusiness(id: number): Observable<LineOfBusiness[]> {
        const url = `${this._coreApiPath}/${id}/lob`;
        return this.http.get<LineOfBusiness[]>(url);
    }

    // ** QmAssociationsController.QmAssociation
    getQmAssociation(id: number): Observable<QmAssociationDto> {
        return this.http.get<QmAssociationDto>(`${this._coreApiPath}/${id}`);
    }

    // ** QmAssociationsController.GetClientPortalNetworkVisibility
    getClientPortalVisibility(id: number): Observable<ClientPortalVisibilitySettings> {
        return this.http.get<ClientPortalVisibilitySettings>(`${this._coreApiPath}/${id}/client-portal-visibility`);
    }

    // ** QmAssociationsController.UpdateClientPortalNetworkVisibility
    updateClientPortalVisibility(id: number, updates: ClientPortalVisibilityUpdate[]): Observable<unknown> {
        return this.http.put(`${this._coreApiPath}/${id}/client-portal-visibility`, updates);
    }

    // ** QmAssociationsController.CreateQmAssociation
    createQmAssociation(QmAssociation: QmAssociationChange): Observable<unknown> {
        return this.http.post(`${this._coreApiPath}/create`, QmAssociation);
    }

    // ** QmAssociationsController.GetDocDueDate
    getDocDueDate(qmAssociationId: number): Observable<DocDueDate> {
        return this.http.get<DocDueDate>(`${this._coreApiPath}/${qmAssociationId}/doc-due-date`);
    }

    // ** QmAssociationsController.UpdateDocDueDate
    updateDocDueDate(qmAssociationId: number, docDueDate: DocDueDate): Observable<unknown> {
        return this.http.put(`${this._coreApiPath}/${qmAssociationId}/doc-due-date`, docDueDate);
    }

    // ** QmAssociationsController.UpdateCompleteStatus
    updateCompleteStatus(qmAssociationId: number, isComplete: boolean): Observable<unknown> {
        return this.http.put(`${this._coreApiPath}/${qmAssociationId}/complete`, isComplete);
    }

    // ** QmAssociationsController.UpdateQmAssociationAssignments
    updateQmAssociationAssignment(qmAssociationUpdates: BulkQmAssociationAssignmentChange): Observable<unknown> {
        return this.http.put(`${this._coreApiPath}/assign`, qmAssociationUpdates);
    }

    // ** QmAssociationsController.UpdateQmAssociationName
    updateQmAssociationName(id: number, qmAssociation: QmAssociationChange): Observable<unknown> {
        return this.http.put(`${this._coreApiPath}/${id}/name`, qmAssociation);
    }

    // ** QmAssociationsController.GetAutoMatchSettingsAsync
    getAutoMatchSettings(id: number): Observable<QmAssociationAutoMatchSettingsViewModel> {
        return this.http.get<QmAssociationAutoMatchSettingsViewModel>(`${this._coreApiPath}/${id}/automatch-settings`);
    }

    // ** QmAssociationsController.UpdateAutoMatchSettingsAsync
    updateAutoMatchSettings(settings: QmAssociationAutoMatchSettingsViewModel): Observable<void> {
        return this.http.put<void>(`${this._coreApiPath}/automatch-settings`, settings);
    }
}
